/**
 * Hara Studio — theme JS
 * - Cart (AJAX add/remove/update + drawer)
 * - Filter tabs on featured products
 * - Spotlight product variant selector
 * - Bestsellers rail scroll buttons
 * - Wishlist (localStorage toggle, visual only)
 */
(function () {
  "use strict";

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const fmtMoney = (cents) => {
    const v = (cents / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return "£" + v;
  };

  /* ========== Cart ========== */
  const Cart = {
    drawer: null,
    backdrop: null,
    state: { items: [], item_count: 0, total_price: 0 },

    init() {
      this.drawer = $("[data-cart-drawer]");
      this.backdrop = $("[data-cart-backdrop]");
      if (!this.drawer) return;

      $$("[data-cart-open]").forEach((el) => el.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      }));
      $$("[data-cart-close]").forEach((el) => el.addEventListener("click", () => this.close()));
      if (this.backdrop) this.backdrop.addEventListener("click", () => this.close());
      document.addEventListener("keydown", (e) => { if (e.key === "Escape") this.close(); });

      this.refresh();
    },

    open() {
      this.drawer.classList.add("open");
      if (this.backdrop) this.backdrop.classList.add("open");
      document.body.style.overflow = "hidden";
    },
    close() {
      this.drawer.classList.remove("open");
      if (this.backdrop) this.backdrop.classList.remove("open");
      document.body.style.overflow = "";
    },

    async refresh() {
      try {
        const res = await fetch("/cart.js");
        this.state = await res.json();
        this.render();
      } catch (e) { console.warn("cart fetch failed", e); }
    },

    async add(variantId, qty) {
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: variantId, quantity: qty || 1 }),
        });
        if (!res.ok) throw new Error(await res.text());
        await this.refresh();
        this.open();
      } catch (e) {
        console.error("Add to cart failed", e);
        alert("Sorry — couldn't add that to bag. Please try again.");
      }
    },

    async change(key, qty) {
      try {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        await this.refresh();
      } catch (e) { console.error(e); }
    },

    render() {
      // Bag count badges
      $$("[data-cart-count]").forEach((el) => {
        const c = this.state.item_count;
        el.textContent = c;
        el.style.display = c > 0 ? "" : "none";
      });
      const drawerCount = $("[data-drawer-count]");
      if (drawerCount) drawerCount.textContent = `${this.state.item_count} ${this.state.item_count === 1 ? "item" : "items"}`;

      // Items
      const itemsEl = $("[data-cart-items]");
      const footerEl = $("[data-cart-footer]");
      if (!itemsEl) return;

      if (this.state.items.length === 0) {
        itemsEl.innerHTML = `
          <div class="bag-empty">
            <p>Your bag is empty.</p>
            <button class="btn btn-ghost" data-cart-close>Browse pieces</button>
          </div>`;
        $$("[data-cart-close]", itemsEl).forEach((el) => el.addEventListener("click", () => this.close()));
        if (footerEl) footerEl.style.display = "none";
        return;
      }

      if (footerEl) footerEl.style.display = "";
      itemsEl.innerHTML = this.state.items.map((it) => `
        <div class="bag-item" data-key="${it.key}">
          <div class="bag-item-img">
            ${it.image ? `<img src="${it.image}" alt="${it.product_title}" style="width:100%;height:100%;object-fit:cover">` : ""}
          </div>
          <div class="bag-item-info">
            <div>
              <div class="bag-item-top">
                <div class="bag-item-name">${it.product_title}</div>
                <div class="bag-item-price">${fmtMoney(it.final_line_price)}</div>
              </div>
              <div class="bag-item-meta">${it.variant_title && it.variant_title !== "Default Title" ? it.variant_title : ""}</div>
            </div>
            <div class="bag-item-bottom">
              <div class="qty">
                <button data-qty-down aria-label="Decrease">−</button>
                <span class="val">${it.quantity}</span>
                <button data-qty-up aria-label="Increase">+</button>
              </div>
              <button class="bag-remove" data-remove>Remove</button>
            </div>
          </div>
        </div>
      `).join("");

      // Wire buttons
      $$(".bag-item", itemsEl).forEach((row) => {
        const key = row.dataset.key;
        const item = this.state.items.find((i) => i.key === key);
        $("[data-qty-up]", row).addEventListener("click", () => this.change(key, item.quantity + 1));
        $("[data-qty-down]", row).addEventListener("click", () => this.change(key, item.quantity - 1));
        $("[data-remove]", row).addEventListener("click", () => this.change(key, 0));
      });

      // Totals
      const subtotalEl = $("[data-cart-subtotal]");
      const totalEl = $("[data-cart-total]");
      const shippingEl = $("[data-cart-shipping]");
      const threshold = parseInt(this.drawer.dataset.shippingThreshold, 10) || 10000;
      const subtotal = this.state.total_price;
      const shipping = subtotal >= threshold || subtotal === 0 ? 0 : 500;
      if (subtotalEl) subtotalEl.textContent = fmtMoney(subtotal);
      if (shippingEl) shippingEl.textContent = shipping === 0 ? "Free" : fmtMoney(shipping);
      if (totalEl) totalEl.textContent = fmtMoney(subtotal + shipping);
    },
  };

  /* ========== Filter tabs (Featured products) ========== */
  const Filters = {
    init() {
      $$("[data-filter-tabs]").forEach((tabsEl) => {
        if (tabsEl.dataset.bound) return;
        tabsEl.dataset.bound = "1";
        const wrap = $(tabsEl.dataset.target);
        if (!wrap) return;
        $$("[data-panel]", tabsEl).forEach((btn) => {
          btn.addEventListener("click", () => {
            const key = btn.dataset.panel;
            $$("[data-panel]", tabsEl).forEach((b) => b.classList.toggle("active", b === btn));
            $$("[data-panel-content]", wrap).forEach((panel) => {
              panel.hidden = panel.dataset.panelContent !== key;
            });
          });
        });
      });
    },
  };

  /* ========== Quick add (with Material / Size chips) ========== */
  const QuickAdd = {
    init() {
      $$("[data-qa]").forEach((root) => {
        if (root.dataset.bound) return;
        root.dataset.bound = "1";
        let variants, optionNames;
        try {
          variants = JSON.parse(root.dataset.qaVariants);
          optionNames = JSON.parse(root.dataset.qaOptions);
        } catch (e) { return; }

        const firstAvail = variants.find((v) => v.available) || variants[0];
        const state = {};
        const qaOptVal = (v, i) => v["option" + (i + 1)];
        optionNames.forEach((name, i) => { state[name] = qaOptVal(firstAvail, i); });

        const findVariant = () =>
          variants.find((v) => optionNames.every((n, i) => qaOptVal(v, i) === state[n]));

        const addBtn = $("[data-qa-add]", root);
        const addLabel = $("[data-qa-add-label]", root);

        const sync = () => {
          // chip active + availability
          $$("[data-qa-opt]", root).forEach((chip) => {
            const n = chip.dataset.qaOpt, val = chip.dataset.qaVal;
            chip.classList.toggle("active", state[n] === val);
          });
          const variant = findVariant();
          if (variant && variant.available) {
            addBtn.dataset.variantId = variant.id;
            addBtn.disabled = false;
            if (addLabel) addLabel.textContent = "Add — " + fmtMoney(variant.price);
          } else if (variant) {
            addBtn.disabled = true;
            if (addLabel) addLabel.textContent = "Sold out";
          }
        };

        $$("[data-qa-opt]", root).forEach((chip) => {
          chip.addEventListener("click", (e) => {
            e.preventDefault(); e.stopPropagation();
            state[chip.dataset.qaOpt] = chip.dataset.qaVal;
            sync();
          });
        });

        if (addBtn) {
          addBtn.addEventListener("click", (e) => {
            e.preventDefault(); e.stopPropagation();
            const variant = findVariant();
            if (!variant || !variant.available) return;
            Cart.add(variant.id, 1);
            if (addLabel) {
              const prev = addLabel.textContent;
              addLabel.textContent = "Added ✓";
              setTimeout(() => { addLabel.textContent = prev; }, 1200);
            }
          });
        }

        sync();
      });
    },
  };

  /* ========== Spotlight ========== */
  const Spotlight = {
    init() {
      $$("[data-spotlight]").forEach((root) => {
        if (root.dataset.bound) return;
        root.dataset.bound = "1";
        // ----- Thumbnail gallery (always wire, independent of product) -----
        const mainImg = $("[data-spotlight-main]", root);
        $$("[data-spotlight-thumb]", root).forEach((thumb) => {
          thumb.addEventListener("click", () => {
            $$("[data-spotlight-thumb]", root).forEach((t) => t.classList.remove("active"));
            thumb.classList.add("active");
            const full = thumb.dataset.full || (thumb.querySelector("img") && thumb.querySelector("img").src);
            if (mainImg && full) mainImg.src = full;
          });
        });

        // ----- Cosmetic chips (no variant logic — single-select per row) -----
        $$("[data-spotlight-cosmetic]", root).forEach((chip) => {
          chip.addEventListener("click", () => {
            const row = chip.closest(".spotlight-option-row");
            if (row) $$("[data-spotlight-cosmetic]", row).forEach((c) => c.classList.remove("active"));
            chip.classList.add("active");
          });
        });

        // ----- Real variant options (Section Rendering API — see ProductForm
        // notes on data-variant-radios for why we don't preload a variants
        // array here: Liquid caps product.variants at 250) -----
        const addBtn = $("[data-spotlight-add]", root);
        const isManual = root.dataset.spManual === "true";
        const productUrl = root.dataset.productUrl;
        const sectionId = root.dataset.sectionId;
        const spotlightOptionsWrap = $(".spotlight-options", root);
        const hasRealOptions = productUrl && sectionId &&
          (root.querySelector("[data-spotlight-option]") || root.querySelector("[data-spotlight-select]"));

        if (hasRealOptions) {
          const collectSelectedIds = () => {
            const ids = [];
            $$("[data-spotlight-select]", root).forEach((sel) => { if (sel.value) ids.push(sel.value); });
            $$(".spotlight-options .opt-chips", root).forEach((group) => {
              const active = group.querySelector(".opt-chip.active");
              if (active) ids.push(active.dataset.optionValueId);
            });
            return ids;
          };

          const applyFetchedDoc = (doc) => {
            const freshPrice = doc.querySelector("[data-spotlight-price]");
            const livePrice = $("[data-spotlight-price]", root);
            if (freshPrice && livePrice) livePrice.textContent = freshPrice.textContent;

            const freshAdd = doc.querySelector("[data-spotlight-add]");
            if (freshAdd && addBtn) {
              addBtn.innerHTML = freshAdd.innerHTML;
              addBtn.disabled = freshAdd.disabled;
              addBtn.dataset.variantId = freshAdd.dataset.variantId;
            }
            const freshOptions = doc.querySelector(".spotlight-options");
            if (freshOptions && spotlightOptionsWrap) {
              spotlightOptionsWrap.innerHTML = freshOptions.innerHTML;
              bindOptionControls();
            }
            if (!isManual) {
              const freshMain = doc.querySelector("[data-spotlight-main]");
              if (freshMain && mainImg && freshMain.src) mainImg.src = freshMain.src;
            }
          };

          const sync = () => {
            const ids = collectSelectedIds();
            const url = `${productUrl}?section_id=${sectionId}&option_values=${ids.join(",")}`;
            fetch(url)
              .then((r) => r.text())
              .then((html) => applyFetchedDoc(new DOMParser().parseFromString(html, "text/html")))
              .catch(() => {});
          };

          const bindOptionControls = () => {
            $$("[data-spotlight-select]", root).forEach((sel) => {
              if (sel.dataset.bound) return;
              sel.dataset.bound = "1";
              sel.addEventListener("change", sync);
            });
            $$("[data-spotlight-option]", root).forEach((chip) => {
              if (chip.dataset.bound) return;
              chip.dataset.bound = "1";
              chip.addEventListener("click", () => {
                if (chip.disabled) return;
                const group = chip.closest(".opt-chips");
                $$(".opt-chip", group).forEach((c) => c.classList.remove("active"));
                chip.classList.add("active");
                sync();
              });
            });
          };

          bindOptionControls();
        }

        // ----- Add to bag -----
        if (addBtn) {
          addBtn.addEventListener("click", (e) => {
            e.preventDefault();
            if (addBtn.disabled) return;
            const id = addBtn.dataset.variantId;
            if (!id) return;
            Cart.add(id, 1);
            const addLabel = $("[data-spotlight-add-label]", addBtn);
            if (addLabel) {
              const prev = addLabel.textContent;
              addLabel.textContent = "Added to bag ✓";
              setTimeout(() => { addLabel.textContent = prev; }, 1500);
            }
          });
        }
      });
    },
  };

  /* ========== Bestsellers rail scroll ========== */
  const Rail = {
    init() {
      $$("[data-rail]").forEach((rail) => {
        if (rail.dataset.bound) return;
        rail.dataset.bound = "1";
        const railTarget = $(rail.dataset.target);
        if (!railTarget) return;
        const dir = parseInt(rail.dataset.dir || "1", 10);
        rail.addEventListener("click", () => {
          const card = railTarget.querySelector(".product");
          const step = card ? card.getBoundingClientRect().width + 20 : 300;
          railTarget.scrollBy({ left: dir * step * 2, behavior: "smooth" });
        });
      });
    },
  };

  /* ========== Footer accordion (mobile) ========== */
  /* ========== Mobile nav ========== */
  const MobileNav = {
    init() {
      const btn = document.querySelector("[data-burger]");
      const nav = document.getElementById("mobile-nav");
      const header = document.querySelector(".site-header");
      if (!btn || !nav || btn.dataset.bound) return;
      btn.dataset.bound = "1";
      let openedAt = 0;
      const open = () => {
        openedAt = Date.now();
        nav.classList.add("open");
        nav.scrollTop = 0;
        requestAnimationFrame(() => nav.classList.add("visible"));
        btn.setAttribute("aria-expanded", "true");
        nav.setAttribute("aria-hidden", "false");
        header.classList.add("nav-open");
        document.body.style.overflow = "hidden";
      };
      const close = () => {
        // Swallow any close attempt that arrives within 600ms of opening —
        // covers ghost clicks, double-fires, and any third-party handlers.
        if (Date.now() - openedAt < 600) return;
        nav.classList.remove("visible");
        nav.addEventListener("transitionend", () => nav.classList.remove("open"), { once: true });
        btn.setAttribute("aria-expanded", "false");
        nav.setAttribute("aria-hidden", "true");
        header.classList.remove("nav-open");
        document.body.style.overflow = "";
      };
      btn.addEventListener("click", () => {
        btn.getAttribute("aria-expanded") === "true" ? close() : open();
      });
      nav.querySelectorAll(".mobile-nav-item:not([data-nav-parent]), .mobile-nav-sub-item").forEach((a) => {
        a.addEventListener("click", close);
      });
      const closeBtn = nav.querySelector("[data-burger-close]");
      if (closeBtn) closeBtn.addEventListener("click", close);
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && btn.getAttribute("aria-expanded") === "true") close();
      });
    },
  };

  const FooterAccordion = {
    init() {
      $$(".footer-col-toggle").forEach((btn) => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = "1";
        btn.addEventListener("click", () => {
          const col = btn.closest(".footer-col");
          const open = col.classList.toggle("open");
          btn.setAttribute("aria-expanded", String(open));
        });
      });
    },
  };

  /* ========== Rail creep touch pause (CSS animation handles the creep) ========== */
  const RailCreep = {
    init() {
      $$(".bestsellers-rail").forEach((rail) => {
        if (rail.dataset.touchBound) return;
        rail.dataset.touchBound = "1";
        const track = rail.querySelector(".rail-track");
        if (!track) return;
        let timer;
        rail.addEventListener("touchstart", () => {
          track.style.animationPlayState = "paused";
          clearTimeout(timer);
        }, { passive: true });
        rail.addEventListener("touchend", () => {
          timer = setTimeout(() => { track.style.animationPlayState = ""; }, 2000);
        }, { passive: true });
      });
    },
  };

  /* ========== Wishlist (visual only, localStorage) ========== */
  const Wishlist = {
    init() {
      const stored = JSON.parse(localStorage.getItem("hara-wishlist") || "[]");
      const setStored = (arr) => localStorage.setItem("hara-wishlist", JSON.stringify(arr));
      $$("[data-wishlist]").forEach((btn) => {
        const pid = btn.dataset.wishlist;
        if (stored.includes(pid)) btn.classList.add("active");
        if (btn.dataset.bound) return;
        btn.dataset.bound = "1";
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const cur = JSON.parse(localStorage.getItem("hara-wishlist") || "[]");
          const i = cur.indexOf(pid);
          if (i >= 0) { cur.splice(i, 1); btn.classList.remove("active"); }
          else { cur.push(pid); btn.classList.add("active"); }
          setStored(cur);
        });
      });
    },
  };

  /* ========== Product form (full product page → drawer) ========== */
  const ProductForm = {
    init() {
      $$("[data-product-form]").forEach((form) => {
        if (form.dataset.bound) return;
        form.dataset.bound = "1";
        form.addEventListener("submit", async (e) => {
          e.preventDefault();
          const btn = form.querySelector("[type=submit]");
          const fd = new FormData(form);
          try {
            const res = await fetch("/cart/add.js", {
              method: "POST",
              headers: { Accept: "application/json" },
              body: fd,
            });
            if (!res.ok) throw new Error(await res.text());
            await Cart.refresh();
            Cart.open();
            if (btn) {
              const label = btn.querySelector("[data-add-label]") || btn;
              const prev = label.textContent;
              label.textContent = "Added to bag ✓";
              setTimeout(() => { label.textContent = prev; }, 1500);
            }
          } catch (err) {
            console.error("Add failed", err);
            alert("Sorry — couldn't add that to bag.");
          }
        });
      });

      // Variant pickers on the product page.
      // Uses the Section Rendering API (option_values param) instead of a
      // preloaded variants list, because Liquid caps `product.variants` at
      // 250 — high-variant products (e.g. size x stamp letter) silently lose
      // combinations past that cap otherwise. See:
      // https://shopify.dev/docs/storefronts/themes/product-merchandising/variants/support-high-variant-products
      $$("[data-variant-radios]").forEach((root) => {
        if (root.dataset.pdpBound) return;
        root.dataset.pdpBound = "1";

        const productUrl = root.dataset.productUrl;
        const sectionId = root.dataset.sectionId;
        const mainImg = root.querySelector("[data-spotlight-main]");
        const optionsWrap = root.querySelector(".spotlight-options");
        const SWAP_SELECTORS = [
          "[data-variant-id-input]",
          "[data-variant-price]",
          "[data-add-submit]",
          "[data-variant-material]",
          "[data-variant-availability]",
        ];

        const collectSelectedIds = () => {
          const ids = [];
          $$("[data-opt-select]", root).forEach((sel) => { if (sel.value) ids.push(sel.value); });
          $$(".opt-chips", root).forEach((group) => {
            const active = group.querySelector(".opt-chip.active");
            if (active) ids.push(active.dataset.optionValueId);
          });
          return ids;
        };

        const applyFetchedDoc = (doc) => {
          SWAP_SELECTORS.forEach((sel) => {
            const fresh = doc.querySelector(sel);
            const live = root.querySelector(sel);
            if (!fresh || !live) return;
            if (live.tagName === "INPUT") live.value = fresh.value;
            else live.innerHTML = fresh.innerHTML;
            live.disabled = fresh.disabled;
          });
          const freshOptions = doc.querySelector(".spotlight-options");
          if (freshOptions && optionsWrap) {
            optionsWrap.innerHTML = freshOptions.innerHTML;
            bindOptionControls();
          }
          const freshMain = doc.querySelector("[data-spotlight-main]");
          if (freshMain && mainImg && freshMain.src) mainImg.src = freshMain.src;
        };

        const sync = () => {
          if (!productUrl || !sectionId) return;
          const ids = collectSelectedIds();
          const url = `${productUrl}?section_id=${sectionId}&option_values=${ids.join(",")}`;
          fetch(url)
            .then((r) => r.text())
            .then((html) => applyFetchedDoc(new DOMParser().parseFromString(html, "text/html")))
            .catch(() => {});
        };

        const bindOptionControls = () => {
          $$("[data-opt-select]", root).forEach((sel) => {
            if (sel.dataset.bound) return;
            sel.dataset.bound = "1";
            sel.addEventListener("change", sync);
          });
          $$(".opt-chip", root).forEach((chip) => {
            if (chip.dataset.bound) return;
            chip.dataset.bound = "1";
            chip.addEventListener("click", () => {
              if (chip.disabled) return;
              const group = chip.closest(".opt-chips");
              $$(".opt-chip", group).forEach((c) => c.classList.remove("active"));
              chip.classList.add("active");
              sync();
            });
          });
        };

        bindOptionControls();
      });
    },
  };

  /* ========== Mobile nav sub-menu accordion ========== */
  const NavAccordion = {
    init() {
      $$("[data-nav-parent]").forEach((btn) => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = "1";
        const sub = btn.nextElementSibling;
        btn.addEventListener("click", () => {
          const open = btn.getAttribute("aria-expanded") === "true";
          btn.setAttribute("aria-expanded", String(!open));
          sub.hidden = open;
        });
      });
    },
  };

  document.addEventListener("DOMContentLoaded", () => {
    Cart.init();
    Filters.init();
    QuickAdd.init();
    Spotlight.init();
    Rail.init();
    RailCreep.init();
    Wishlist.init();
    ProductForm.init();
    FooterAccordion.init();
    MobileNav.init();
    NavAccordion.init();
  });

  // Shopify customizer re-renders a section over AJAX (no full page reload),
  // which drops event handlers. Re-init the section-scoped modules; the
  // per-element `data-bound` guards above keep this from double-binding.
  document.addEventListener("shopify:section:load", () => {
    Filters.init();
    QuickAdd.init();
    Spotlight.init();
    Rail.init();
    Wishlist.init();
    ProductForm.init();
    MobileNav.init();
    NavAccordion.init();
  });

  window.HaraCart = { add: (id, qty) => Cart.add(id, qty) };
})();
